import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  useUserChoices,
  getGroupByKey,
  getSelectViewKey,
} from './useUserChoices';

const SESSION_KEY_PREFIX = 'pageState_';

/**
 * Configuration options for useExplicitPageState hook.
 */
export interface UseExplicitPageStateOptions<G, V> {
  pageKey: string;
  groupOptions: G[];
  viewOptions: V[];
  firmDefaultGroup?: string;
  firmDefaultView?: string;
  getGroupValue: (option: G) => string;
  getViewValue: (option: V) => string;
}

/**
 * Return type for useExplicitPageState hook.
 */
export interface UseExplicitPageStateReturn<G, V> {
  // Current session state
  currentGroup: G;
  setCurrentGroup: (group: G) => void;
  currentView: V;
  setCurrentView: (view: V) => void;

  // Saved state info
  savedGroupValue: string | undefined;
  savedViewValue: string | undefined;
  firmDefaultGroupValue: string;
  firmDefaultViewValue: string | undefined;

  // Actions
  saveGroupAsDefault: () => void;
  saveViewAsDefault: () => void;
  resetGroupToFirmDefault: () => void;
  resetViewToFirmDefault: () => void;

  // Status
  hasUnsavedGroupChange: boolean;
  hasUnsavedViewChange: boolean;
  isUsingFirmDefaultGroup: boolean;
  isUsingFirmDefaultView: boolean;
  isLoading: boolean;
  isInitialized: boolean;
}

/**
 * Hook for page state with explicit save behavior.
 *
 * Unlike usePersistedPageState which auto-saves on every change,
 * this hook keeps changes in session only until explicitly saved.
 *
 * Resolution priority on load:
 * 1. Session storage (if user changed it this session)
 * 2. User's saved preference (from server)
 * 3. Firm default (from menu configuration)
 * 4. First option in the list (fallback)
 */
export function useExplicitPageState<G, V>(
  options: UseExplicitPageStateOptions<G, V>,
): UseExplicitPageStateReturn<G, V> {
  const {
    pageKey,
    groupOptions,
    viewOptions,
    firmDefaultGroup,
    firmDefaultView,
    getGroupValue,
    getViewValue,
  } = options;

  const {
    getValue,
    setValue,
    clearValue,
    isLoading: isSettingsLoading,
    isSettingsReady,
  } = useUserChoices();
  const hasInitialized = useRef(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const groupKey = getGroupByKey(pageKey);
  const viewKey = getSelectViewKey(pageKey);
  const sessionGroupKey = `${SESSION_KEY_PREFIX}${pageKey}_group`;
  const sessionViewKey = `${SESSION_KEY_PREFIX}${pageKey}_view`;

  // Helper to find option by value
  const findGroupByValue = useCallback(
    (value: string | undefined): G | undefined => {
      if (!value) return undefined;
      return groupOptions.find((opt) => getGroupValue(opt) === value);
    },
    [groupOptions, getGroupValue],
  );

  const findViewByValue = useCallback(
    (value: string | undefined): V | undefined => {
      if (!value) return undefined;
      return viewOptions.find((opt) => getViewValue(opt) === value);
    },
    [viewOptions, getViewValue],
  );

  // Get saved values from server
  const savedGroupValue = useMemo(() => {
    if (!isSettingsReady) return undefined;
    return getValue(groupKey);
  }, [isSettingsReady, getValue, groupKey]);

  const savedViewValue = useMemo(() => {
    if (!isSettingsReady) return undefined;
    return getValue(viewKey);
  }, [isSettingsReady, getValue, viewKey]);

  // Determine effective default (saved or firm)
  const effectiveDefaultGroup = useMemo(() => {
    if (savedGroupValue) {
      const match = findGroupByValue(savedGroupValue);
      if (match) return match;
    }
    if (firmDefaultGroup) {
      const match = findGroupByValue(firmDefaultGroup);
      if (match) return match;
    }
    return groupOptions[0];
  }, [savedGroupValue, firmDefaultGroup, findGroupByValue, groupOptions]);

  const effectiveDefaultView = useMemo(() => {
    if (savedViewValue) {
      const match = findViewByValue(savedViewValue);
      if (match) return match;
    }
    if (firmDefaultView) {
      const match = findViewByValue(firmDefaultView);
      if (match) return match;
    }
    return viewOptions[0];
  }, [savedViewValue, firmDefaultView, findViewByValue, viewOptions]);

  // Current session state
  const [currentGroup, setCurrentGroupState] = useState<G>(
    effectiveDefaultGroup,
  );
  const [currentView, setCurrentViewState] = useState<V>(effectiveDefaultView);

  // Initialize from session storage or saved preference
  useEffect(() => {
    if (!isSettingsReady || hasInitialized.current) return;
    if (groupOptions.length === 0 || viewOptions.length === 0) return;

    // Check session storage first
    const sessionGroup = sessionStorage.getItem(sessionGroupKey);
    const sessionView = sessionStorage.getItem(sessionViewKey);

    let initialGroup: G | undefined;
    let initialView: V | undefined;

    // Try session storage first
    if (sessionGroup) {
      initialGroup = findGroupByValue(sessionGroup);
    }
    if (sessionView) {
      initialView = findViewByValue(sessionView);
    }

    // Fall back to saved preference or firm default
    if (!initialGroup) {
      initialGroup = effectiveDefaultGroup;
    }
    if (!initialView) {
      initialView = effectiveDefaultView;
    }

    setCurrentGroupState(initialGroup);
    setCurrentViewState(initialView);
    hasInitialized.current = true;
    setIsInitialized(true);
  }, [
    isSettingsReady,
    groupOptions,
    viewOptions,
    sessionGroupKey,
    sessionViewKey,
    findGroupByValue,
    findViewByValue,
    effectiveDefaultGroup,
    effectiveDefaultView,
  ]);

  // Re-apply when groupOptions change (for async custom tags)
  useEffect(() => {
    if (!hasInitialized.current || groupOptions.length === 0) return;

    const sessionGroup = sessionStorage.getItem(sessionGroupKey);
    if (sessionGroup) {
      const match = findGroupByValue(sessionGroup);
      if (match && getGroupValue(match) !== getGroupValue(currentGroup)) {
        setCurrentGroupState(match);
      }
    }
  }, [
    groupOptions,
    sessionGroupKey,
    findGroupByValue,
    getGroupValue,
    currentGroup,
  ]);

  // Set current group (session only)
  const setCurrentGroup = useCallback(
    (group: G) => {
      setCurrentGroupState(group);
      sessionStorage.setItem(sessionGroupKey, getGroupValue(group));
    },
    [sessionGroupKey, getGroupValue],
  );

  // Set current view (session only)
  const setCurrentView = useCallback(
    (view: V) => {
      setCurrentViewState(view);
      sessionStorage.setItem(sessionViewKey, getViewValue(view));
    },
    [sessionViewKey, getViewValue],
  );

  // Save current group as default (persist to server)
  const saveGroupAsDefault = useCallback(() => {
    const value = getGroupValue(currentGroup);
    setValue(groupKey, value);
  }, [currentGroup, getGroupValue, setValue, groupKey]);

  // Save current view as default (persist to server)
  // NOTE (2/2/26): Product decided to disable user-level view default persistence.
  // This function is preserved for potential future re-enablement but should NOT
  // be exposed in UI components. View selection remains session-only.
  const saveViewAsDefault = useCallback(() => {
    const value = getViewValue(currentView);
    setValue(viewKey, value);
  }, [currentView, getViewValue, setValue, viewKey]);

  // Reset group to firm default (clear saved preference)
  const resetGroupToFirmDefault = useCallback(() => {
    clearValue(groupKey);
    sessionStorage.removeItem(sessionGroupKey);

    // Apply firm default
    const firmMatch = findGroupByValue(firmDefaultGroup);
    setCurrentGroupState(firmMatch ?? groupOptions[0]);
  }, [
    clearValue,
    groupKey,
    sessionGroupKey,
    findGroupByValue,
    firmDefaultGroup,
    groupOptions,
  ]);

  // Reset view to firm default (clear saved preference)
  // NOTE (2/2/26): Product decided to disable user-level view default persistence.
  // This function is preserved for potential future re-enablement but should NOT
  // be exposed in UI components.
  const resetViewToFirmDefault = useCallback(() => {
    clearValue(viewKey);
    sessionStorage.removeItem(sessionViewKey);

    // Apply firm default
    const firmMatch = findViewByValue(firmDefaultView);
    setCurrentViewState(firmMatch ?? viewOptions[0]);
  }, [
    clearValue,
    viewKey,
    sessionViewKey,
    findViewByValue,
    firmDefaultView,
    viewOptions,
  ]);

  // Compute status flags
  const isUsingFirmDefaultGroup = savedGroupValue === undefined;
  const isUsingFirmDefaultView = savedViewValue === undefined;

  const currentGroupValue = getGroupValue(currentGroup);
  const currentViewValue = getViewValue(currentView);

  // Determine what the "saved" state is (user's saved or firm default)
  const effectiveGroupDefault =
    savedGroupValue ?? firmDefaultGroup ?? getGroupValue(groupOptions[0]);
  const effectiveViewDefault =
    savedViewValue ?? firmDefaultView ?? getViewValue(viewOptions[0]);

  const hasUnsavedGroupChange = currentGroupValue !== effectiveGroupDefault;
  const hasUnsavedViewChange = currentViewValue !== effectiveViewDefault;

  return {
    currentGroup,
    setCurrentGroup,
    currentView,
    setCurrentView,
    savedGroupValue,
    savedViewValue,
    firmDefaultGroupValue: firmDefaultGroup ?? 'none',
    firmDefaultViewValue: firmDefaultView,
    saveGroupAsDefault,
    saveViewAsDefault,
    resetGroupToFirmDefault,
    resetViewToFirmDefault,
    hasUnsavedGroupChange,
    hasUnsavedViewChange,
    isUsingFirmDefaultGroup,
    isUsingFirmDefaultView,
    isLoading: isSettingsLoading,
    isInitialized,
  };
}

export default useExplicitPageState;
